import type { InjectionToken } from "tsyringe";
import type { IDate } from "../types/date";
import type { IProduct } from "../types/entities";
import { type Action, type Measures } from "../types/entities";
import { AiApiModule } from "./modules/AiApiModule";
import { AuthApiModule } from "./modules/AuthApiModule";
import { AuditApiModule } from "./modules/AuditApiModule";
import { BoxApiModule } from "./modules/BoxApiModule";
import { DashboardApiModule } from "./modules/DashboardApiModule";
import { JournalApiModule } from "./modules/JournalApiModule";
import { PushApiModule } from "./modules/PushApiModule";
import { ProductApiModule } from "./modules/ProductApiModule";
import {
  QrBindingApiModule,
  QrBindingConflictError,
} from "./modules/QrBindingApiModule";
import { UserApiModule } from "./modules/UserApiModule";
import { WarehouseHttpClient } from "./modules/WarehouseHttpClient";
import {
  extractApiError,
  normalizeAdminDashboardSummary,
  normalizeAiFormFillResult,
  normalizeAuthIdentity,
  normalizeAuthResponse,
  normalizeAuthUser,
  normalizeBoxListItem,
  normalizeBoxProductItem,
  normalizeDashboardStats,
  normalizeDeletedBoxItem,
  normalizeDeletedProductItem,
  normalizeJournalEntry,
  normalizePagedPayload,
  normalizeProduct,
  normalizeRecentJournalFeedEntry,
  normalizeSoftDeleteAuditItem,
  normalizeTypeTags,
  normalizeUserListItem,
  toSafeNonNegativeInt,
} from "./modules/WarehouseApiMappers";
import {
  clearStoredSession,
  getStoredRefreshToken,
  setStoredSession,
} from "../utils/authStorage";

export type ProductPlacementFilter = "all" | "inBox" | "withoutBox";
export type ProductExpiryFilter = "all" | "expiringSoon" | "expired";

export interface ProductsPageInput {
  limit?: number;
  offset?: number;
  placement?: ProductPlacementFilter;
  expiry?: ProductExpiryFilter;
  expiringDays?: number;
  q?: string;
  typeTag?: string;
  measure?: "all" | Measures;
}

export interface BoxesPageInput {
  limit?: number;
  offset?: number;
  q?: string;
  typeTag?: string;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number | null;
}

export interface ProductJournalEntry {
  id: string;
  date: IDate | null;
  userId: string | null;
  userName: string | null;
  userRole: "admin" | "user" | "viewer" | null;
  actorType: "user" | "system";
  actorLabel: string;
  action: Action;
  productId: string;
  fromTo: string;
  value: number;
  comment: string | null;
}

export interface RecentProductJournalFeedEntry extends ProductJournalEntry {
  eventType?: "journal";
  productName: string | null;
  boxName: string | null;
}

export interface BoxRestoredJournalFeedEntry {
  id: string;
  eventType: "box.restored";
  date: IDate | null;
  userId: string | null;
  userName: string | null;
  userRole: "admin" | "user" | "viewer" | null;
  actorType: "user" | "system";
  actorLabel: string;
  boxId: string;
  boxName: string | null;
  comment: string | null;
  source: string | null;
}

export type RecentJournalFeedEntry =
  | RecentProductJournalFeedEntry
  | BoxRestoredJournalFeedEntry;

export interface DashboardStats {
  productsTotal: number;
  boxesTotal: number;
  productsInBoxes: number;
  productsWithoutBox: number;
  expiringSoon: number;
  expired: number;
}

export interface AdminDashboardSummary {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  deletedProducts: number;
  deletedBoxes: number;
}

export interface BoxListItem {
  id: string;
  name: string;
  photo: string;
  typeTags?: string[];
  description: string;
  parentId: string | null;
}

export interface BoxProductItem {
  id: string;
  name: string;
  photo: string;
  photos?: string[];
  description: string;
  typeTags?: string[];
  measure: Measures;
  quantity: number;
  parentId: string | null;
  manufacturingDate: IDate | null;
  expiryDate: IDate | null;
}

export interface BoxContentsData {
  box: BoxListItem;
  contents: {
    boxes: BoxListItem[];
    products: BoxProductItem[];
  };
}

export interface DeletedProductItem extends IProduct {
  deletedAt: string | null;
  deletedBy: string | null;
  deleteReason: string | null;
}

export interface DeletedBoxItem extends BoxListItem {
  deletedAt: string | null;
  deletedBy: string | null;
  deleteReason: string | null;
}

export interface SoftDeleteAuditItem {
  id: string;
  entityType: "product" | "box";
  entityId: string;
  entityName: string | null;
  parentBoxId: string | null;
  actorUserId: string | null;
  actorUserName: string | null;
  source: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string | null;
}

export interface CreateProductInput {
  name: string;
  photo?: string;
  photos?: string[];
  description?: string;
  manufacturingDate?: IDate | null;
  expiryDate?: IDate | null;
  typeTags?: string[];
  measure: Measures;
  quantity: number;
  parentId: string;
}

export interface UpdateProductInput {
  name: string;
  photo?: string;
  photos?: string[];
  description?: string;
  manufacturingDate?: IDate | null;
  expiryDate?: IDate | null;
  typeTags?: string[];
  measure: Measures;
  quantity: number;
  parentId?: string | null;
}

export interface CreateJournalEntryInput {
  action: Action;
  productId: string;
  fromTo: string;
  value?: number;
  comment?: string;
  userId?: string | null;
  date?: IDate | null;
}

export interface UpdateBoxInput {
  name: string;
  photo?: string;
  description?: string;
  typeTags?: string[];
  parentId?: string | null;
}

export interface CreateBoxInput {
  name: string;
  photo?: string;
  description?: string;
  typeTags?: string[];
  parentId?: string | null;
}

export interface JournalSearchInput {
  action?: Action;
  productId?: string;
  fromTo?: string;
  userId?: string;
  userRole?: "admin" | "user" | "viewer";
  limit?: number;
  offset?: number;
}

export type AiFormEntity = "product" | "box";

export interface AiFormFillInput {
  entity: AiFormEntity;
  transcript?: string;
  photo?: string | null;
}

export interface AiProductDraft {
  name: string | null;
  description: string | null;
  typeTags: string[];
  parentId: string | null;
  parentHint: string | null;
  measure: Measures | null;
  quantity: number | null;
  manufacturingDate: string | null;
  expiryDate: string | null;
  photo: string | null;
}

export interface AiBoxDraft {
  name: string | null;
  description: string | null;
  typeTags: string[];
  parentId: string | null;
  parentHint: string | null;
  photo: string | null;
}

export interface AiFormFillResult {
  entity: AiFormEntity;
  model: string;
  draft: AiProductDraft | AiBoxDraft;
}

export interface AuthUser {
  id: string;
  name: string;
  email?: string | null;
  photo: string;
  role: "admin" | "user" | "viewer";
  status?: "pending" | "active";
}

export interface AuthResponse {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RegisterPendingResponse {
  pending: true;
  message: string;
  user: AuthUser;
}

export type RegisterResponse = AuthResponse | RegisterPendingResponse;

export type AuthIdentityProvider = "email" | "telegram" | "google" | "apple";

export interface AuthIdentity {
  provider: AuthIdentityProvider;
  providerUserId: string;
  providerLogin: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TelegramWidgetAuthInput {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export interface UserListItem {
  id: string;
  name: string;
  email?: string | null;
  photo: string;
  role: "admin" | "user" | "viewer";
  status: "pending" | "active";
  lastActive: IDate | null;
}

export interface UpdateUserProfileInput {
  name?: string;
  photo?: string;
}

export interface LoginInput {
  username: string;
  password: string;
}

export interface RegisterInput extends LoginInput {
  email?: string;
  photo?: string;
}

export interface WebPushSubscriptionInput {
  endpoint: string;
  expirationTime?: number | null;
  remindersEnabled?: boolean;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface WebPushPublicKeyResponse {
  enabled: boolean;
  publicKey: string | null;
}

export interface WebPushSendStats {
  delivered: number;
  failed: number;
  expired: number;
}

export interface QrBindingRecord {
  payload: string;
  entityId: string;
  entityType: "product" | "box";
  createdAt: string;
  updatedAt: string;
}

export interface ResolveQrPayloadResult {
  entityId: string;
  entityType: "product" | "box";
}

export interface BindQrPayloadInput {
  entityId: string;
  payload: string;
}

export interface IWarehouseApi {
  getDashboardStats(expiringDays?: number): Promise<DashboardStats>;
  getAdminDashboardSummary(): Promise<AdminDashboardSummary>;
  getAllProducts(): Promise<IProduct[]>;
  getProductsPage(input?: ProductsPageInput): Promise<PagedResult<IProduct>>;
  getProductById(id: string): Promise<IProduct | null>;
  getAllBoxes(): Promise<BoxListItem[]>;
  getBoxesPage(input?: BoxesPageInput): Promise<PagedResult<BoxListItem>>;
  getBoxById(id: string): Promise<BoxListItem | null>;
  searchProducts(query: string): Promise<IProduct[]>;
  searchBoxes(query: string): Promise<BoxListItem[]>;
  createProduct(input: CreateProductInput): Promise<IProduct>;
  createBox(input: CreateBoxInput): Promise<BoxListItem>;
  updateProduct(id: string, input: UpdateProductInput): Promise<IProduct>;
  updateBox(id: string, input: UpdateBoxInput): Promise<BoxListItem>;
  deleteBox(id: string, deleteReason?: string | null): Promise<void>;
  deleteProduct(id: string, deleteReason?: string | null): Promise<void>;
  getRecentJournalEntries(limit?: number): Promise<ProductJournalEntry[]>;
  getRecentJournalFeed(limit?: number): Promise<RecentJournalFeedEntry[]>;
  getJournalEntriesByProduct(
    productId: string,
    limit?: number,
  ): Promise<ProductJournalEntry[]>;
  getJournalEntriesByBox(
    boxId: string,
    limit?: number,
  ): Promise<ProductJournalEntry[]>;
  searchJournalEntries(
    input?: JournalSearchInput,
  ): Promise<ProductJournalEntry[]>;
  fillFormDraft(input: AiFormFillInput): Promise<AiFormFillResult>;
  createJournalEntry(
    input: CreateJournalEntryInput,
  ): Promise<ProductJournalEntry>;
  getBoxContents(boxId: string): Promise<BoxContentsData>;
  getDeletedProducts(input?: {
    limit?: number;
    offset?: number;
  }): Promise<PagedResult<DeletedProductItem>>;
  getDeletedBoxes(input?: {
    limit?: number;
    offset?: number;
  }): Promise<PagedResult<DeletedBoxItem>>;
  getSoftDeleteAudit(input?: {
    entityType?: "product" | "box";
    entityId?: string;
    actorUserId?: string;
    limit?: number;
    offset?: number;
  }): Promise<PagedResult<SoftDeleteAuditItem>>;
  restoreProduct(id: string): Promise<void>;
  hardDeleteProduct(id: string): Promise<void>;
  restoreBox(id: string): Promise<void>;
  hardDeleteBox(id: string): Promise<void>;
  login(input: LoginInput): Promise<AuthResponse>;
  loginWithTelegram(
    initData: string,
  ): Promise<AuthResponse | RegisterPendingResponse>;
  loginWithTelegramWidget(
    payload: TelegramWidgetAuthInput,
  ): Promise<AuthResponse | RegisterPendingResponse>;
  loginWithGoogleIdToken(
    idToken: string,
  ): Promise<AuthResponse | RegisterPendingResponse>;
  loginWithAppleIdToken(
    idToken: string,
  ): Promise<AuthResponse | RegisterPendingResponse>;
  register(input: RegisterInput): Promise<RegisterResponse>;
  getAuthIdentities(): Promise<AuthIdentity[]>;
  linkEmailIdentity(email: string): Promise<void>;
  linkTelegramIdentity(initData: string): Promise<void>;
  linkTelegramIdentityWidget(payload: TelegramWidgetAuthInput): Promise<void>;
  linkGoogleIdentity(idToken: string): Promise<void>;
  linkAppleIdentity(idToken: string): Promise<void>;
  unlinkIdentity(provider: AuthIdentity["provider"]): Promise<void>;
  getUserById(userId: string): Promise<AuthUser>;
  updateUserProfile(
    userId: string,
    input: UpdateUserProfileInput,
  ): Promise<AuthUser>;
  getUsers(): Promise<UserListItem[]>;
  getPendingUsersCount(): Promise<number>;
  approveUser(userId: string): Promise<void>;
  deleteUser(userId: string): Promise<void>;
  getWebPushPublicKey(): Promise<WebPushPublicKeyResponse>;
  subscribeWebPush(input: WebPushSubscriptionInput): Promise<void>;
  unsubscribeWebPush(endpoint: string): Promise<void>;
  sendWebPushTest(input?: {
    title?: string;
    body?: string;
  }): Promise<WebPushSendStats>;
  getQrBindingByEntity(entityId: string): Promise<QrBindingRecord | null>;
  resolveQrPayload(payload: string): Promise<ResolveQrPayloadResult | null>;
  bindQrPayload(input: BindQrPayloadInput): Promise<QrBindingRecord>;
  logout(): Promise<void>;
}

export const WAREHOUSE_API_TOKEN = Symbol(
  "IWarehouseApi",
) as InjectionToken<IWarehouseApi>;

export class WarehouseApi implements IWarehouseApi {
  private readonly httpClient = new WarehouseHttpClient({ extractApiError });

  private readonly dashboardApi = new DashboardApiModule(
    this.httpClient,
    normalizeDashboardStats,
    normalizeAdminDashboardSummary,
  );

  private readonly authApi = new AuthApiModule(this.httpClient, {
    extractApiError,
    normalizeAuthResponse,
    normalizeAuthUser,
    normalizeAuthIdentity,
    persistSessionFromAuth: (response) => {
      setStoredSession({
        ...response.user,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn,
      });
    },
    clearStoredSession: () => {
      clearStoredSession();
    },
    getStoredRefreshToken: () => getStoredRefreshToken(),
  });

  private readonly productApi = new ProductApiModule(this.httpClient, {
    extractApiError,
    normalizeProduct,
    normalizeDeletedProductItem,
    normalizeTypeTags,
    toSafeNonNegativeInt,
    normalizePagedPayload,
  });

  private readonly boxApi = new BoxApiModule(this.httpClient, {
    extractApiError,
    normalizeBoxListItem,
    normalizeBoxProductItem,
    normalizeDeletedBoxItem,
    normalizeTypeTags,
    toSafeNonNegativeInt,
    normalizePagedPayload,
  });

  private readonly journalApi = new JournalApiModule(this.httpClient, {
    extractApiError,
    normalizeJournalEntry,
    normalizeRecentJournalFeedEntry,
  });

  private readonly userApi = new UserApiModule(this.httpClient, {
    extractApiError,
    normalizeAuthUser,
    normalizeUserListItem,
    toSafeNonNegativeInt,
  });

  private readonly pushApi = new PushApiModule(this.httpClient, {
    extractApiError,
    toSafeNonNegativeInt,
  });

  private readonly aiApi = new AiApiModule(this.httpClient, {
    normalizeAiFormFillResult,
  });

  private readonly auditApi = new AuditApiModule(this.httpClient, {
    extractApiError,
    normalizeSoftDeleteAuditItem,
    toSafeNonNegativeInt,
  });

  private readonly qrBindingApi = new QrBindingApiModule(this.httpClient, {
    extractApiError,
  });

  async getDashboardStats(expiringDays = 7): Promise<DashboardStats> {
    return this.dashboardApi.getDashboardStats(expiringDays);
  }

  async getAdminDashboardSummary(): Promise<AdminDashboardSummary> {
    return this.dashboardApi.getAdminDashboardSummary();
  }

  async getAllProducts(): Promise<IProduct[]> {
    return this.productApi.getAllProducts();
  }

  async getProductsPage(
    input: ProductsPageInput = {},
  ): Promise<PagedResult<IProduct>> {
    return this.productApi.getProductsPage(input);
  }

  async getProductById(id: string): Promise<IProduct | null> {
    return this.productApi.getProductById(id);
  }

  async getAllBoxes(): Promise<BoxListItem[]> {
    return this.boxApi.getAllBoxes();
  }

  async getBoxesPage(
    input: BoxesPageInput = {},
  ): Promise<PagedResult<BoxListItem>> {
    return this.boxApi.getBoxesPage(input);
  }

  async getBoxById(id: string): Promise<BoxListItem | null> {
    return this.boxApi.getBoxById(id);
  }

  async searchProducts(query: string): Promise<IProduct[]> {
    return this.productApi.searchProducts(query);
  }

  async searchBoxes(query: string): Promise<BoxListItem[]> {
    return this.boxApi.searchBoxes(query);
  }

  async fillFormDraft(input: AiFormFillInput): Promise<AiFormFillResult> {
    return this.aiApi.fillFormDraft(input);
  }

  async createProduct(input: CreateProductInput): Promise<IProduct> {
    return this.productApi.createProduct(input);
  }

  async createBox(input: CreateBoxInput): Promise<BoxListItem> {
    return this.boxApi.createBox(input);
  }

  async updateProduct(
    id: string,
    input: UpdateProductInput,
  ): Promise<IProduct> {
    return this.productApi.updateProduct(id, input);
  }

  async updateBox(id: string, input: UpdateBoxInput): Promise<BoxListItem> {
    return this.boxApi.updateBox(id, input);
  }

  async deleteBox(id: string, deleteReason?: string | null): Promise<void> {
    return this.boxApi.deleteBox(id, deleteReason);
  }

  async deleteProduct(id: string, deleteReason?: string | null): Promise<void> {
    return this.productApi.deleteProduct(id, deleteReason);
  }

  async getJournalEntriesByProduct(
    productId: string,
    limit = 50,
  ): Promise<ProductJournalEntry[]> {
    return this.journalApi.getJournalEntriesByProduct(productId, limit);
  }

  async getJournalEntriesByBox(
    boxId: string,
    limit = 50,
  ): Promise<ProductJournalEntry[]> {
    return this.journalApi.getJournalEntriesByBox(boxId, limit);
  }

  async getRecentJournalEntries(limit = 20): Promise<ProductJournalEntry[]> {
    return this.journalApi.getRecentJournalEntries(limit);
  }

  async getRecentJournalFeed(limit = 20): Promise<RecentJournalFeedEntry[]> {
    return this.journalApi.getRecentJournalFeed(limit);
  }

  async searchJournalEntries(
    input: JournalSearchInput = {},
  ): Promise<ProductJournalEntry[]> {
    return this.journalApi.searchJournalEntries(input);
  }

  async login(input: LoginInput): Promise<AuthResponse> {
    return this.authApi.login(input);
  }

  async loginWithTelegram(
    initData: string,
  ): Promise<AuthResponse | RegisterPendingResponse> {
    return this.authApi.loginWithTelegram(initData);
  }

  async loginWithTelegramWidget(
    payload: TelegramWidgetAuthInput,
  ): Promise<AuthResponse | RegisterPendingResponse> {
    return this.authApi.loginWithTelegramWidget(payload);
  }

  async loginWithGoogleIdToken(
    idToken: string,
  ): Promise<AuthResponse | RegisterPendingResponse> {
    return this.authApi.loginWithGoogleIdToken(idToken);
  }

  async loginWithAppleIdToken(
    idToken: string,
  ): Promise<AuthResponse | RegisterPendingResponse> {
    return this.authApi.loginWithAppleIdToken(idToken);
  }

  async register(input: RegisterInput): Promise<RegisterResponse> {
    return this.authApi.register(input);
  }

  async getAuthIdentities(): Promise<AuthIdentity[]> {
    return this.authApi.getAuthIdentities();
  }

  async linkEmailIdentity(email: string): Promise<void> {
    return this.authApi.linkEmailIdentity(email);
  }

  async linkTelegramIdentity(initData: string): Promise<void> {
    return this.authApi.linkTelegramIdentity(initData);
  }

  async linkTelegramIdentityWidget(
    payload: TelegramWidgetAuthInput,
  ): Promise<void> {
    return this.authApi.linkTelegramIdentityWidget(payload);
  }

  async linkGoogleIdentity(idToken: string): Promise<void> {
    return this.authApi.linkGoogleIdentity(idToken);
  }

  async linkAppleIdentity(idToken: string): Promise<void> {
    return this.authApi.linkAppleIdentity(idToken);
  }

  async unlinkIdentity(provider: AuthIdentity["provider"]): Promise<void> {
    return this.authApi.unlinkIdentity(provider);
  }

  async getUserById(userId: string): Promise<AuthUser> {
    return this.userApi.getUserById(userId);
  }

  async updateUserProfile(
    userId: string,
    input: UpdateUserProfileInput,
  ): Promise<AuthUser> {
    return this.userApi.updateUserProfile(userId, input);
  }

  async getUsers(): Promise<UserListItem[]> {
    return this.userApi.getUsers();
  }

  async getPendingUsersCount(): Promise<number> {
    return this.userApi.getPendingUsersCount();
  }

  async approveUser(userId: string): Promise<void> {
    return this.userApi.approveUser(userId);
  }

  async deleteUser(userId: string): Promise<void> {
    return this.userApi.deleteUser(userId);
  }

  async getWebPushPublicKey(): Promise<WebPushPublicKeyResponse> {
    return this.pushApi.getWebPushPublicKey();
  }

  async subscribeWebPush(input: WebPushSubscriptionInput): Promise<void> {
    return this.pushApi.subscribeWebPush(input);
  }

  async unsubscribeWebPush(endpoint: string): Promise<void> {
    return this.pushApi.unsubscribeWebPush(endpoint);
  }

  async sendWebPushTest(input?: {
    title?: string;
    body?: string;
  }): Promise<WebPushSendStats> {
    return this.pushApi.sendWebPushTest(input);
  }

  async getQrBindingByEntity(
    entityId: string,
  ): Promise<QrBindingRecord | null> {
    return this.qrBindingApi.getQrBindingByEntity(entityId);
  }

  async resolveQrPayload(
    payload: string,
  ): Promise<ResolveQrPayloadResult | null> {
    return this.qrBindingApi.resolveQrPayload(payload);
  }

  async bindQrPayload(input: BindQrPayloadInput): Promise<QrBindingRecord> {
    return this.qrBindingApi.bindQrPayload(input);
  }

  async logout(): Promise<void> {
    return this.authApi.logout();
  }

  async createJournalEntry(
    input: CreateJournalEntryInput,
  ): Promise<ProductJournalEntry> {
    return this.journalApi.createJournalEntry(input);
  }

  async getBoxContents(boxId: string): Promise<BoxContentsData> {
    return this.boxApi.getBoxContents(boxId);
  }

  async getDeletedProducts(
    input: { limit?: number; offset?: number } = {},
  ): Promise<PagedResult<DeletedProductItem>> {
    return this.productApi.getDeletedProducts(input);
  }

  async getDeletedBoxes(
    input: { limit?: number; offset?: number } = {},
  ): Promise<PagedResult<DeletedBoxItem>> {
    return this.boxApi.getDeletedBoxes(input);
  }

  async getSoftDeleteAudit(
    input: {
      entityType?: "product" | "box";
      entityId?: string;
      actorUserId?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<PagedResult<SoftDeleteAuditItem>> {
    return this.auditApi.getSoftDeleteAudit(input);
  }

  async restoreProduct(id: string): Promise<void> {
    return this.productApi.restoreProduct(id);
  }

  async hardDeleteProduct(id: string): Promise<void> {
    return this.productApi.hardDeleteProduct(id);
  }

  async restoreBox(id: string): Promise<void> {
    return this.boxApi.restoreBox(id);
  }

  async hardDeleteBox(id: string): Promise<void> {
    return this.boxApi.hardDeleteBox(id);
  }
}

export { QrBindingConflictError };
